import React, { useEffect, useRef } from 'react';

import { useIntl } from 'react-intl-next';

import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useExtensionsState } from '@atlassian/company-hub-common';
import { ImageRenderer } from '@atlassian/company-hub-common/media-components';
import {
	SpotlightContent,
	type SpotlightContentProps,
} from '@atlassian/company-hub-common/spotlight-content';
import { type LayoutProps, SpotlightLayout } from '@atlassian/company-hub-common/spotlight-layout';

import { ARIA_CONTROLS_PREFIX } from '../../../constants';
import { frameNewParametersV2 } from '../../../controllers/use-carousel-extension-parameters-and-actions';
import { CommonLayout, type Frame } from '../../../types';

import { messages } from './messages';

type ImageSource = Frame['imageSrc'];

const getImage = (src: ImageSource, contentId: string, alt?: string) => {
	return src ? (
		<ImageRenderer
			contentId={contentId}
			imageSrc={src}
			imageAlt={alt}
			shouldShowExternalSiteImage
		/>
	) : null;
};

const styles = cssMap({
	frameStyles: {
		flex: 1,
		// force current frame to stay aligned with container
		// even during window resizes
		scrollSnapAlign: 'start',
	},
});

export type CarouselLayoutRendererProps = {
	contentId: string;
	extensionLocalId?: string;
	frameSetLength?: number;
	imageAltText?: string;
	imageData: ImageSource;
	index?: number;
	selectedFrameIndex?: number;
} & Pick<
	SpotlightContentProps,
	'testId' | 'source' | 'width' | 'link' | 'title' | 'description' | 'buttonStyle' | 'buttonText'
> &
	Partial<Pick<SpotlightContentProps, 'textColor'>> &
	Pick<LayoutProps, 'height' | 'imageWidthPercentage'> &
	Partial<Pick<LayoutProps, 'backgroundColor' | 'layout' | 'hasRoundedCorners'>>;

export const CarouselLayoutRenderer: React.FC<CarouselLayoutRendererProps> = ({
	testId,
	contentId,
	source,
	link,
	title,
	description,
	// default to neutral color for extensions who don't have this param set yet
	// (aka v1 carousels), otherwise BG color will be set to a different default
	// upon insertion, when Carousel V2 is enabled
	backgroundColor = token('color.background.input'),
	textColor = frameNewParametersV2['textColor'],
	height,
	layout = CommonLayout.SPLIT_IMAGE_LEFT,
	imageData,
	imageAltText,
	width,
	imageWidthPercentage,
	buttonStyle = frameNewParametersV2['buttonStyle'],
	buttonText,
	hasRoundedCorners,
	index = 0,
	selectedFrameIndex = -1,
	frameSetLength = 1,
	extensionLocalId,
}) => {
	const intl = useIntl();
	const cardRef = useRef<HTMLDivElement>(null);
	const [{ selectedExtensionLocalId }] = useExtensionsState();

	useEffect(() => {
		if (
			cardRef.current &&
			selectedFrameIndex === index &&
			(!selectedExtensionLocalId || extensionLocalId === selectedExtensionLocalId)
		) {
			cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
		}
	}, [selectedFrameIndex, index, extensionLocalId, selectedExtensionLocalId]);

	return (
		<Box
			xcss={cx(styles.frameStyles)}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
			style={{ width, minWidth: '100%' }}
			aria-label={intl.formatMessage(messages.frameSpotlightLabel, {
				positionInSet: index + 1,
				setSize: frameSetLength,
			})}
			ref={cardRef}
			id={`${ARIA_CONTROLS_PREFIX}${extensionLocalId}${index}`}
		>
			<SpotlightLayout
				testId={testId}
				backgroundColor={backgroundColor}
				height={height}
				layout={layout}
				image={getImage(imageData, contentId, imageAltText)}
				width={width}
				imageWidthPercentage={imageWidthPercentage}
				hasRoundedCorners={hasRoundedCorners}
			>
				<SpotlightContent
					title={title}
					description={description}
					layout={layout}
					textColor={textColor}
					isButtonShown={!!link}
					buttonStyle={buttonStyle}
					buttonText={buttonText}
					link={link}
					width={width}
					source={source}
					imageWidthPercentage={imageWidthPercentage}
				/>
			</SpotlightLayout>
		</Box>
	);
};
